// 图书搜索和获取管理器

import type {
  BookSource,
  BookInfo,
  ChapterContent,
  SearchQuery,
  SearchResult
} from '@/types/reading';

import { BookParser } from './bookParser';
import { BookSourceManager } from './bookSourceManager';
import type { StorageManager } from './storage';

export class BookSearchManager {
  private parser: BookParser;
  private sourceManager: BookSourceManager;
  private storage: StorageManager;
  private searchCache: Map<string, { result: SearchResult; timestamp: number }> = new Map();
  private bookCache: Map<string, { book: BookInfo; timestamp: number }> = new Map();
  private readonly CACHE_TTL = 30 * 60 * 1000; // 30分钟缓存

  constructor(sourceManager: BookSourceManager, storage: StorageManager) {
    this.parser = new BookParser();
    this.sourceManager = sourceManager;
    this.storage = storage;
  }

  // ============================================================================
  // 多书源搜索
  // ============================================================================

  async searchBooksAcrossSources(query: SearchQuery): Promise<SearchResult> {
    try {
      // 1. 获取启用的书源
      const enabledSources = await this.sourceManager.getEnabledBookSources();
      
      if (enabledSources.length === 0) {
        return this.createEmptyResult(query);
      }

      // 2. 检查缓存
      const cacheKey = this.generateSearchCacheKey(query);
      const cached = this.searchCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
        return cached.result;
      }

      // 3. 并行搜索所有书源
      const searchPromises = enabledSources.map(source => 
        this.searchSingleSource(query, source)
      );

      const results = await Promise.allSettled(searchPromises);
      
      // 4. 合并结果
      const allBooks: BookInfo[] = [];
      
      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value.books.length > 0) {
          allBooks.push(...result.value.books);
        } else if (result.status === 'rejected') {
          console.warn(`Search failed for source ${enabledSources[index].bookSourceName}:`, result.reason);
        }
      });

      // 5. 去重和合并相同图书
      const mergedBooks = this.mergeAndDeduplicateBooks(allBooks);
      
      // 6. 排序和分页
      const sortedBooks = this.sortBooks(mergedBooks, query.sortBy || 'relevance', query.keyword);
      const startIndex = (query.page - 1) * query.pageSize;
      const endIndex = startIndex + query.pageSize;
      const paginatedBooks = sortedBooks.slice(startIndex, endIndex);

      const finalResult: SearchResult = {
        books: paginatedBooks,
        total: sortedBooks.length,
        page: query.page,
        pageSize: query.pageSize,
        suggestions: this.generateSearchSuggestions(query.keyword, allBooks),
        searchTime: Date.now()
      };

      // 7. 缓存结果
      this.searchCache.set(cacheKey, { result: finalResult, timestamp: Date.now() });
      
      return finalResult;

    } catch (error) {
      console.error('Multi-source search failed:', error);
      return this.createEmptyResult(query);
    }
  }

  private async searchSingleSource(query: SearchQuery, source: BookSource): Promise<SearchResult> {
    try {
      // 限制单个书源的搜索时间
      const timeoutPromise = new Promise<SearchResult>((_, reject) => {
        setTimeout(() => reject(new Error('Search timeout')), 10000);
      });

      const searchPromise = this.parser.searchBooks(query, source);
      
      return await Promise.race([searchPromise, timeoutPromise]);

    } catch (error) {
      console.warn(`Search failed for source ${source.bookSourceName}:`, error);
      return this.createEmptyResult(query);
    }
  }

  // ============================================================================
  // 图书详情获取
  // ============================================================================

  async getBookDetail(bookId: string): Promise<BookInfo | null> {
    try {
      // 1. 检查缓存
      const cached = this.bookCache.get(bookId);
      if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
        return cached.book;
      }

      // 2. 从存储中获取基本信息
      let bookInfo = await this.storage.getBookInfo(bookId);
      
      if (!bookInfo) {
        return null;
      }

      // 3. 获取书源信息
      const source = await this.sourceManager.getBookSource(bookInfo.sourceId);
      if (!source) {
        return bookInfo; // 返回基本信息，即使书源不可用
      }

      // 4. 如果需要，从书源获取最新详情
      if (this.shouldRefreshBookDetail(bookInfo)) {
        const freshBookInfo = await this.parser.getBookDetail(bookInfo.sourceUrl, source);
        if (freshBookInfo) {
          // 合并信息，保留本地的ID和其他元数据
          bookInfo = {
            ...freshBookInfo,
            id: bookInfo.id,
            sourceId: bookInfo.sourceId
          };
          
          // 更新存储
          await this.storage.saveBookInfo(bookInfo);
        }
      }

      // 5. 缓存结果
      this.bookCache.set(bookId, { book: bookInfo, timestamp: Date.now() });
      
      return bookInfo;

    } catch (error) {
      console.error('Get book detail failed:', error);
      return null;
    }
  }

  private shouldRefreshBookDetail(bookInfo: BookInfo): boolean {
    // 如果图书信息超过24小时没有更新，则刷新
    const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
    return bookInfo.updateTime.getTime() < dayAgo;
  }

  // ============================================================================
  // 章节内容获取
  // ============================================================================

  async getChapterContent(bookId: string, chapterId: string): Promise<ChapterContent | null> {
    try {
      // 1. 获取图书信息
      const bookInfo = await this.getBookDetail(bookId);
      if (!bookInfo) {
        return null;
      }

      // 2. 找到对应章节
      const chapter = bookInfo.chapters.find(ch => ch.id === chapterId);
      if (!chapter) {
        return null;
      }

      // 3. 检查缓存
      const cacheKey = `chapter_${bookId}_${chapterId}`;
      const cachedContent = await this.storage.getCacheItem('chapter', cacheKey);
      if (cachedContent) {
        return cachedContent;
      }

      // 4. 获取书源信息
      const source = await this.sourceManager.getBookSource(bookInfo.sourceId);
      if (!source) {
        return null;
      }

      // 5. 从书源获取章节内容
      const content = await this.parser.getChapterContent(chapter.url, source);
      
      if (content) {
        // 6. 缓存内容（24小时）
        await this.storage.setCacheItem('chapter', cacheKey, content, 24);
      }

      return content;

    } catch (error) {
      console.error('Get chapter content failed:', error);
      return null;
    }
  }

  // ============================================================================
  // 图书发现和推荐
  // ============================================================================

  async getPopularBooks(limit: number = 20): Promise<BookInfo[]> {
    try {
      const enabledSources = await this.sourceManager.getEnabledBookSources();
      const allBooks: BookInfo[] = [];

      // 从每个书源获取热门图书
      for (const source of enabledSources.slice(0, 5)) { // 限制书源数量
        try {
          // 使用通用搜索词获取热门图书
          const popularQuery: SearchQuery = {
            keyword: '', // 空关键词通常返回热门图书
            sortBy: 'popularity',
            page: 1,
            pageSize: 10
          };

          const result = await this.searchSingleSource(popularQuery, source);
          allBooks.push(...result.books);

        } catch (error) {
          console.warn(`Failed to get popular books from ${source.bookSourceName}:`, error);
        }
      }

      // 去重和排序
      const mergedBooks = this.mergeAndDeduplicateBooks(allBooks);
      return mergedBooks.slice(0, limit);

    } catch (error) {
      console.error('Get popular books failed:', error);
      return [];
    }
  }

  async getBooksByCategory(category: string, page: number = 1, pageSize: number = 20): Promise<SearchResult> {
    const query: SearchQuery = {
      keyword: '',
      category,
      page,
      pageSize,
      sortBy: 'popularity'
    };

    return await this.searchBooksAcrossSources(query);
  }

  async getBooksByAuthor(author: string, page: number = 1, pageSize: number = 20): Promise<SearchResult> {
    const query: SearchQuery = {
      keyword: '',
      author,
      page,
      pageSize,
      sortBy: 'updateTime'
    };

    return await this.searchBooksAcrossSources(query);
  }

  // ============================================================================
  // 数据处理和工具方法
  // ============================================================================

  private mergeAndDeduplicateBooks(books: BookInfo[]): BookInfo[] {
    const bookMap = new Map<string, BookInfo>();
    
    for (const book of books) {
      // 使用标题和作者作为去重键
      const key = `${book.title.toLowerCase()}_${book.author.toLowerCase()}`;
      
      const existing = bookMap.get(key);
      if (!existing) {
        bookMap.set(key, book);
      } else {
        // 合并相同图书的信息
        const merged = this.mergeBookInfo(existing, book);
        bookMap.set(key, merged);
      }
    }
    
    return Array.from(bookMap.values());
  }

  private mergeBookInfo(book1: BookInfo, book2: BookInfo): BookInfo {
    // 选择信息更完整的图书作为基础
    const base = book1.description && book1.coverUrl ? book1 : book2;
    const other = base === book1 ? book2 : book1;
    
    return {
      ...base,
      // 合并章节列表（选择更长的）
      chapters: base.chapters.length >= other.chapters.length ? base.chapters : other.chapters,
      // 合并标签
      tags: [...new Set([...base.tags, ...other.tags])],
      // 选择更新的时间
      updateTime: base.updateTime > other.updateTime ? base.updateTime : other.updateTime,
      // 保留更好的封面
      coverUrl: base.coverUrl || other.coverUrl,
      // 保留更详细的描述
      description: base.description || other.description
    };
  }

  private sortBooks(books: BookInfo[], sortBy: string, keyword?: string): BookInfo[] {
    switch (sortBy) {
      case 'rating':
        return books.sort((a, b) => (b.rating || 0) - (a.rating || 0));
      
      case 'updateTime':
        return books.sort((a, b) => b.updateTime.getTime() - a.updateTime.getTime());
      
      case 'wordCount':
        return books.sort((a, b) => (b.wordCount || 0) - (a.wordCount || 0));
      
      case 'popularity':
        return books.sort((a, b) => {
          const scoreA = this.calculatePopularityScore(a);
          const scoreB = this.calculatePopularityScore(b);
          return scoreB - scoreA;
        });
      
      default: // relevance
        if (keyword) {
          return books.sort((a, b) => {
            const scoreA = this.calculateRelevanceScore(a, keyword);
            const scoreB = this.calculateRelevanceScore(b, keyword);
            return scoreB - scoreA;
          });
        }
        return books;
    }
  }

  private calculatePopularityScore(book: BookInfo): number {
    let score = 0;
    
    // 评分权重
    if (book.rating) {
      score += book.rating * 20;
    }
    
    // 字数权重（对数缩放）
    if (book.wordCount) {
      score += Math.log(book.wordCount / 1000) * 5;
    }
    
    // 章节数权重
    score += book.chapters.length * 0.1;
    
    // 更新时间权重（越新越好）
    const daysSinceUpdate = (Date.now() - book.updateTime.getTime()) / (24 * 60 * 60 * 1000);
    score += Math.max(0, 30 - daysSinceUpdate) * 0.5;
    
    return score;
  }

  private calculateRelevanceScore(book: BookInfo, keyword: string): number {
    const keywordLower = keyword.toLowerCase();
    let score = 0;
    
    // 标题匹配
    if (book.title.toLowerCase().includes(keywordLower)) {
      score += 10;
      if (book.title.toLowerCase().startsWith(keywordLower)) {
        score += 5;
      }
    }
    
    // 作者匹配
    if (book.author.toLowerCase().includes(keywordLower)) {
      score += 5;
    }
    
    // 描述匹配
    if (book.description && book.description.toLowerCase().includes(keywordLower)) {
      score += 2;
    }
    
    // 标签匹配
    book.tags.forEach(tag => {
      if (tag.toLowerCase().includes(keywordLower)) {
        score += 1;
      }
    });
    
    return score;
  }

  private generateSearchSuggestions(keyword: string, books: BookInfo[]): string[] {
    const suggestions = new Set<string>();
    
    // 基于搜索结果生成建议
    books.forEach(book => {
      // 添加作者建议
      if (book.author && !book.author.toLowerCase().includes(keyword.toLowerCase())) {
        suggestions.add(book.author);
      }
      
      // 添加标签建议
      book.tags.forEach(tag => {
        if (!tag.toLowerCase().includes(keyword.toLowerCase())) {
          suggestions.add(tag);
        }
      });
    });
    
    // 添加通用建议
    if (keyword.length > 2) {
      suggestions.add(`${keyword} 小说`);
      suggestions.add(`${keyword} 全集`);
    }
    
    return Array.from(suggestions).slice(0, 5);
  }

  private createEmptyResult(query: SearchQuery): SearchResult {
    return {
      books: [],
      total: 0,
      page: query.page,
      pageSize: query.pageSize,
      suggestions: [],
      searchTime: Date.now()
    };
  }

  private generateSearchCacheKey(query: SearchQuery): string {
    return `search_${query.keyword}_${query.author || ''}_${query.category || ''}_${query.sortBy || 'relevance'}_${query.page}_${query.pageSize}`;
  }

  // ============================================================================
  // 缓存管理
  // ============================================================================

  clearSearchCache(): void {
    this.searchCache.clear();
  }

  clearBookCache(): void {
    this.bookCache.clear();
  }

  async clearExpiredCache(): Promise<void> {
    // 清理内存缓存
    const now = Date.now();
    
    for (const [key, value] of this.searchCache.entries()) {
      if (now - value.timestamp > this.CACHE_TTL) {
        this.searchCache.delete(key);
      }
    }
    
    for (const [key, value] of this.bookCache.entries()) {
      if (now - value.timestamp > this.CACHE_TTL) {
        this.bookCache.delete(key);
      }
    }
    
    // 清理存储缓存
    await this.storage.clearExpiredCache();
  }

  getCacheStats(): { searchCache: number; bookCache: number } {
    return {
      searchCache: this.searchCache.size,
      bookCache: this.bookCache.size
    };
  }
}